import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AppSettings } from '../../app.settings';
import { CommonBindingDataService } from '../../services/common-binding-data.service';
import { BookingService } from 'app/modules/bookings/services/booking.service';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'app-cancellation-reason-popup',
  templateUrl: './cancellation-reason-popup.component.html',
  styleUrls: ['./cancellation-reason-popup.component.scss']
})
export class CancellationReasonPopupComponent implements OnInit, OnChanges {

  @Input() visible: boolean = false;
  @Input() selectedBookingForCancellation: any;
  @Input() header: string = 'label_from_cancel_event';
  @Input() showWarning: boolean = false;
  @Input() eventId: string;

  @Output() onConfirm: EventEmitter<{ passengerNote: string; bookingNote: string }> = new EventEmitter();
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  passengerNote: string = '';
  bookingNote: string = '';
  restrictSpecialSymbol = AppSettings.RESTRICT_ONLY_SPECIAL_SYMBOL;

  showAssociateBookings: boolean = false;
  showPassengerNotification: boolean = true;
  bookingToCancel: any[] = [];
  selectedBookingToCancel: any[] = [];
  showHideSelectAll: boolean = false;
  disabledBookingValues: number[] = [75, 120, 130, 140];
  isSmsEnabled: boolean = false;
  isEmailEnabled: boolean = true;
  sendSms: boolean = false;
  sendEmail: boolean = false;

  constructor(
    private bookingService: BookingService,
    private commonBindingDataService: CommonBindingDataService,
    private uiService: UiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true && this.selectedBookingForCancellation) {
      if (!this.bookingNote && this.selectedBookingForCancellation.tripNote) {
        this.bookingNote = this.selectedBookingForCancellation.tripNote;
      }
      // this.isSmsEnabled = !!this.selectedBookingForCancellation?.smsEnabled;
      // this.isEmailEnabled = !!this.selectedBookingForCancellation?.emailEnabled;
      this.isSmsEnabled = false;
      this.isEmailEnabled = true;
      this.updatePassengerNotificationVisibility();
      this.checkAssociateBookingsCondition();
    }
  }

  private updatePassengerNotificationVisibility(): void {
    const booking = this.selectedBookingForCancellation;
    const subType = booking?.subServiceType;
    const journeyValues = AppSettings.JOURNEY_TYPE_VALUES;

    this.showPassengerNotification = !(
      subType === journeyValues.SHUTTLE ||
      subType === journeyValues.VEHICLE_TRANSFER
    );
  }

  private checkAssociateBookingsCondition(): void {
    const booking = this.selectedBookingForCancellation;
    const isLuggageOrMultiVehicle =
      booking.requestLabelType === AppSettings.LUGGAGE_REQUEST ||
      booking.requestLabelType === AppSettings.MULTIPLE_VEHICLE_REQUEST ||
      booking.requestLabelType === AppSettings.MULTIPLE_VEHICLE_WITH_LUGGAGE_REQUEST;
    const isValidAction = booking.luggageTourAction !== 'new' && booking.luggageTourAction !== 'reject';

    if (booking.passengerPlusLuggageVehicleId) {
      this.fetchBookingsToCancel();
    } else {
      this.showAssociateBookings = false;
    }
  }

  private fetchBookingsToCancel(): void {
    this.bookingToCancel = [];
    const bookingId = this.selectedBookingForCancellation.id;
    this.commonBindingDataService.showBookingsToCancel(bookingId, this.eventId).subscribe(result => {
      this.bookingToCancel = result.associateBookingsList;
      const disableSelectAll: number[] = [];
      const valuesToCheck = [130, 140, 120];

      if (this.bookingToCancel.length > 0) {
        this.bookingToCancel.forEach((element) => {
          disableSelectAll.push(element.tourStatus);
        });
      }

      const foundValues = disableSelectAll.filter(value => valuesToCheck.includes(value));
      this.showHideSelectAll = foundValues.length > 0;
      this.showAssociateBookings = true;
      this.cdr.detectChanges();

      const selectedBooking = this.bookingToCancel.find(
        booking => booking.tourId === bookingId && !valuesToCheck.includes(booking.tourStatus)
      );

      if (selectedBooking) {
        this.selectedBookingToCancel = [selectedBooking];
      }
    }, (error) => {
      this.uiService.showMessage(error.general[0].message, AppSettings.MESSAGE_TYPE.ERROR);
    });
  }

  isBookingDisabled(value: number): boolean {
    return this.disabledBookingValues.includes(value);
  }

  confirm(): void {
    const bookingId = this.selectedBookingForCancellation.id;

    if (this.showAssociateBookings) {
      if (this.selectedBookingToCancel && this.selectedBookingToCancel.length > 0) {
        const selectedBookingIds = this.selectedBookingToCancel.map(booking => booking.tourId);
        if (selectedBookingIds && selectedBookingIds.length > 0) {
          const data = {
            cancellationReason: this.passengerNote,
            tripNote: this.bookingNote,
            tourIds: selectedBookingIds.includes('') ? [] : selectedBookingIds,
            sendSms: this.sendSms,
            sendEmail: this.sendEmail
          };
          this.callCancelApi(bookingId, data);
        } else {
          this.uiService.showMessage('No valid booking IDs selected for cancellation', AppSettings.MESSAGE_TYPE.ERROR);
        }
      } else {
        this.uiService.showMessage('Invalid Action', AppSettings.MESSAGE_TYPE.ERROR);
      }
    } else {
      const data = {
        cancellationReason: this.passengerNote,
        tripNote: this.bookingNote,
        tourIds: [bookingId],
        sendSms: this.sendSms,
        sendEmail: this.sendEmail
      };
      this.callCancelApi(bookingId, data);
    }
  }

  private callCancelApi(bookingId: any, data: any): void {
    this.bookingService.cancelBooking(bookingId, data).subscribe((result) => {
      this.uiService.showMessage(result.general[0].message, AppSettings.MESSAGE_TYPE.SUCCESS);
      this.cancel();
    }, (error) => {
      this.uiService.showMessage(error.general[0].message, AppSettings.MESSAGE_TYPE.ERROR);
    });
  }

  cancel(): void {
    this.onCancel.emit();
    this.resetForm();
    this.closeDialog();
  }

  onHide(): void {
    this.resetForm();
    this.closeDialog();
  }

  private resetForm(): void {
    this.passengerNote = '';
    this.bookingNote = '';
    this.bookingToCancel = [];
    this.selectedBookingToCancel = [];
    this.showAssociateBookings = false;
    this.showHideSelectAll = false;
    this.sendSms = false;
    this.sendEmail = false;
    this.isSmsEnabled = false;
    this.isEmailEnabled = true;
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
